/**
 * 进行中对话轮次的跨刷新持久化（localStorage）。
 *
 * 刷新会销毁 SSE 连接与 `runChatTurn` 闭包内的 `uuid` / `lastEventId`，
 * 这里落盘会话上下文，使页面重新挂载后能：
 * 1. 用 `projectId` 拉 `/chat/history/detail` 回显；
 * 2. 该轮仍 `IN_PROGRESS` 时用 `resumeStream` + `Last-Event-ID` 从游标续推。
 *
 * 用 localStorage 而非 sessionStorage：后者按标签页隔离，新标签页 / 新窗口打开对话页读不到；
 * 跨标签页误恢复由 30 分钟 TTL 与「新建对话」入口的 `clearActiveTurn()` 兜住。
 */

const STORAGE_KEY = 'ai_chat_active_turn';

/** 超过该时长的记录视为陈旧（后端 SSE 通道 15 分钟超时，留一倍余量） */
const MAX_AGE_MS = 30 * 60 * 1000;

/** 游标写入节流间隔：逐帧写 localStorage 无收益 */
const CURSOR_FLUSH_MS = 1000;

export interface PersistedTurn {
  projectId: string;
  title?: string;
  /** 进行中轮次 id；轮次落终态后清空 */
  uuid?: string;
  /** 最后收到的 SSE 帧 id，续推时作为 `Last-Event-ID` */
  lastEventId?: string;
  updatedAt: number;
}

export interface TurnCursor {
  projectId: string;
  uuid: string;
  lastEventId: string;
}

const readRaw = (): PersistedTurn | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedTurn;
    return parsed?.projectId ? parsed : null;
  } catch {
    // 存储不可用 / 脏数据：按无记录处理
    return null;
  }
};

const writeRaw = (next: PersistedTurn) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 存储不可用：持久化能力降级，不影响主流程
  }
};

let pendingCursor: TurnCursor | null = null;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pageHideBound = false;

const clearFlushTimer = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

/** 会话上下文落盘（projectId 变化时丢弃旧轮次游标） */
export const saveActiveTurn = (patch: {
  projectId: string;
  title?: string;
  uuid?: string;
  lastEventId?: string;
}): void => {
  if (!patch.projectId) return;
  const prev = readRaw();
  const base: PersistedTurn =
    prev && prev.projectId === patch.projectId ? prev : { projectId: patch.projectId, updatedAt: 0 };
  writeRaw({ ...base, ...patch, updatedAt: Date.now() });
};

/** 读取未过期的会话上下文；陈旧记录直接清掉 */
export const readActiveTurn = (): PersistedTurn | null => {
  const prev = readRaw();
  if (!prev) return null;
  if (Date.now() - (prev.updatedAt || 0) > MAX_AGE_MS) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // 忽略：无法清理不影响后续判定（调用方按 null 处理）
    }
    return null;
  }
  return prev;
};

const flushCursor = () => {
  clearFlushTimer();
  if (!pendingCursor) return;
  saveActiveTurn(pendingCursor);
  pendingCursor = null;
};

/** 游标落盘（节流 1s，`pagehide` 时立即 flush，尽量少丢帧） */
export const saveTurnCursor = (cursor: TurnCursor): void => {
  if (!cursor.projectId || !cursor.uuid) return;
  pendingCursor = cursor;
  if (!pageHideBound) {
    pageHideBound = true;
    window.addEventListener('pagehide', flushCursor);
  }
  if (flushTimer) return;
  flushTimer = setTimeout(flushCursor, CURSOR_FLUSH_MS);
};

/** 轮次结束 / 打断：清掉续推游标，保留 projectId + title 供刷新后回显历史 */
export const clearTurnCursor = (): void => {
  pendingCursor = null;
  clearFlushTimer();
  const prev = readRaw();
  if (!prev) return;
  writeRaw({ projectId: prev.projectId, title: prev.title, updatedAt: Date.now() });
};

/** 新建对话等"明确要空白会话"的入口调用：整条记录作废，避免刷新恢复到上一个项目 */
export const clearActiveTurn = (): void => {
  pendingCursor = null;
  clearFlushTimer();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 存储不可用：忽略
  }
};
